import React, { useEffect } from 'react';

/**
 * AdSense Display Ad Component
 * 
 * This component will automatically show ads once your AdSense account is approved.
 * Currently, your account is in "Getting ready" status, so ads won't display yet.
 * 
 * @param {string} slot - Ad unit slot ID (optional, for specific ad units)
 * @param {string} format - Ad format: 'auto', 'rectangle', 'horizontal', 'vertical'
 * @param {boolean} responsive - Whether the ad should be responsive
 * @param {string} className - Additional CSS classes
 */
const AdSenseAd = ({ 
  slot = 'auto',
  format = 'auto', 
  responsive = true,
  className = '',
  style = {}
}) => {
  useEffect(() => {
    try {
      // Push ad to AdSense
      if (window.adsbygoogle && process.env.NODE_ENV === 'production') {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, []);

  // Don't show ads in development
  if (process.env.NODE_ENV !== 'production') {
    return (
      <div className={`bg-gray-900 border border-gold/30 rounded-lg p-4 text-center ${className}`}>
        <p className="text-gray-400 text-sm">
          📢 AdSense Ad Placeholder
          <br />
          <span className="text-xs opacity-60">
            (Ads will appear here in production after approval)
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className={`adsense-container ${className}`} style={style}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', ...style }}
        data-ad-client="ca-pub-1162992509433603"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
};

/**
 * In-Article Ad (for blog posts)
 * Best placed between paragraphs in blog content
 */
export const InArticleAd = ({ className = '' }) => (
  <div className={`my-8 ${className}`}>
    <AdSenseAd 
      format="fluid"
      slot="auto"
      className="min-h-[200px]"
    />
  </div>
);

/**
 * Display Ad (rectangle - sidebar or between content)
 */
export const DisplayAd = ({ className = '' }) => (
  <div className={`my-6 ${className}`}>
    <AdSenseAd 
      format="rectangle"
      slot="auto"
      className="min-h-[250px]"
    />
  </div>
);

/**
 * Horizontal Ad (banner style - top or bottom of page)
 */
export const HorizontalAd = ({ className = '' }) => (
  <div className={`my-6 ${className}`}>
    <AdSenseAd 
      format="horizontal"
      slot="auto"
      className="min-h-[100px]"
    />
  </div>
);

/**
 * Responsive Ad (adapts to container size)
 */
export const ResponsiveAd = ({ className = '' }) => (
  <div className={`my-6 ${className}`}>
    <AdSenseAd 
      format="auto"
      responsive={true}
      slot="auto"
      className="min-h-[200px]"
    />
  </div>
);

export default AdSenseAd;
